export function entityKey(value) {
    if (value == null) return null;
    if (typeof value === "string" || typeof value === "number") return value;
    if (value.data) return entityKey(value.data);
    return value.documentId ?? value.id ?? null;
}

export function unwrapRelation(value) {
    if (Array.isArray(value)) return value[0] || null;
    if (Array.isArray(value?.data)) return value.data[0] || null;
    return value?.data || value || null;
}

export function relationItems(value) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.results)) return value.results;
    return value ? [unwrapRelation(value)].filter(Boolean) : [];
}

export function entityLabel(value, fallback = "-") {
    const entity = unwrapRelation(value);
    if (!entity) return fallback;
    if (typeof entity === "string" || typeof entity === "number") return String(entity);
    return entity.name
        || entity.title
        || entity.fullname
        || entity.companyName
        || entity.code
        || entity.documentId
        || entity.id
        || fallback;
}

export function relationIdentifier(value) {
    const entity = unwrapRelation(value);
    return entity?.documentId ?? entity?.id ?? entity?.value ?? entity ?? null;
}

function relationDocument(value) {
    const entity = unwrapRelation(value);
    if (entity == null) return null;
    if (typeof entity === "number") return {id: entity};
    if (typeof entity === "string") return {documentId: entity};
    if (entity.id != null || entity.documentId) {
        return {
            ...(entity.id != null ? {id: entity.id} : {}),
            ...(entity.documentId ? {documentId: entity.documentId} : {}),
        };
    }
    return relationDocument(entity.value);
}

function relationDocumentKey(document) {
    return document?.documentId != null
        ? `document:${document.documentId}`
        : document?.id != null
            ? `id:${document.id}`
            : null;
}

export function relationMutation(nextValue, previousValue = null) {
    const next = relationDocument(nextValue);
    const previous = relationDocument(previousValue);
    const changed = relationDocumentKey(next) !== relationDocumentKey(previous);
    return {
        connect: changed && next ? [next] : [],
        disconnect: changed && previous ? [previous] : [],
    };
}

export function relationConnectMany(values = []) {
    return {
        connect: values.map(relationDocument).filter(Boolean),
        disconnect: [],
    };
}

export function relationMutationMany(nextValues = [], previousValues = []) {
    const nextDocuments = new Map(
        relationItems(nextValues)
            .map(relationDocument)
            .filter(Boolean)
            .map((document) => [relationDocumentKey(document), document])
    );
    const previousDocuments = new Map(
        relationItems(previousValues)
            .map(relationDocument)
            .filter(Boolean)
            .map((document) => [relationDocumentKey(document), document])
    );

    return {
        connect: [...nextDocuments.entries()]
            .filter(([key]) => !previousDocuments.has(key))
            .map(([, document]) => document),
        disconnect: [...previousDocuments.entries()]
            .filter(([key]) => !nextDocuments.has(key))
            .map(([, document]) => document),
    };
}

export function formatNumber(value) {
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? new Intl.NumberFormat("fa-IR").format(number) : "-";
}

const currencyTranslationKeys = {
    IRR: "currency-irr",
    USD: "currency-usd",
    EUR: "currency-eur",
};

export function formatMoney(value, currency = "IRR", translate) {
    if (value == null || value === "") return "-";
    const normalizedCurrency = String(currency || "IRR").toUpperCase();
    const currencyKey = currencyTranslationKeys[normalizedCurrency];
    const currencyLabel = currencyKey && translate ? translate(currencyKey) : normalizedCurrency;
    return `${formatNumber(value)} ${currencyLabel}`;
}

export function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

export function toDateTimeInput(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

export function toISOStringOrNull(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function statusSeverity(status) {
    const value = String(status || "").toLowerCase();
    if (["active", "paid", "succeeded", "completed", "approved", "granted", "applied", "qualified"].includes(value)) return "success";
    if (["pending", "pending_payment", "submitted", "under_review", "trialing", "paused", "reserved", "visited", "registered"].includes(value)) return "warning";
    if (["failed", "rejected", "canceled", "expired", "ended", "void", "refunded", "reversed", "disabled"].includes(value)) return "danger";
    return "info";
}

export function translatedStatus(translate, status) {
    const normalizedStatus = String(status || "").trim().toLowerCase().replace(/_/g, "-");
    if (!normalizedStatus) return translate("sales-status-unknown");
    const key = `sales-status-${normalizedStatus}`;
    const value = translate(key);
    return value === key ? translate("sales-status-unknown") : value;
}

export function includesSearch(values, search) {
    const needle = String(search || "").trim().toLowerCase();
    if (!needle) return true;
    return values.some((value) => String(value ?? "").toLowerCase().includes(needle));
}

export function getErrorMessage(error, fallback = "") {
    return error?.response?.data?.error?.message
        || error?.response?.data?.error
        || error?.message
        || fallback;
}

export function translateOptions(translate, options) {
    return options.map(({labelKey, ...option}) => ({...option, label: translate(labelKey)}));
}

export function slugify(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\u0600-\u06ff]+/g, "-")
        .replace(/^-+|-+$/g, "");
}
